/**
 * Input Validation Service
 * 
 * Validates user inputs for solar system design
 * Prevents invalid data from reaching calculation engines
 */

import type { CalculationInputs } from './solarCalculationEngine';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface SystemInputs {
  systemSize?: number;
  tilt?: number;
  azimuth?: number;
  latitude?: number;
  longitude?: number;
  altitude?: number;
  monthlyConsumption?: number[];
  yearlyConsumption?: number;
  dcCableDistance?: number;
  acCableDistance?: number;
  modulePower?: number;
  email?: string;
  phoneNumber?: string;
}

/**
 * Validate system size (kWp)
 */
function validateSystemSize(size: number | undefined): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (size === undefined || size === null) {
    errors.push('System size is required');
    return { errors, warnings };
  }

  if (typeof size !== 'number' || isNaN(size)) {
    errors.push('System size must be a valid number');
    return { errors, warnings };
  }

  if (size <= 0) {
    errors.push('System size must be greater than 0');
  } else if (size < 1) {
    warnings.push('System size is very small (< 1 kWp). Are you sure?');
  } else if (size > 10000) {
    warnings.push('System size is very large (> 10 MWp). Please verify.');
  }

  return { errors, warnings };
}

/**
 * Validate tilt angle (degrees)
 */
function validateTilt(tilt: number | undefined): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (tilt === undefined || tilt === null) {
    errors.push('Tilt angle is required');
    return { errors, warnings };
  }

  if (typeof tilt !== 'number' || isNaN(tilt)) {
    errors.push('Tilt angle must be a valid number');
    return { errors, warnings };
  }

  if (tilt < 0 || tilt > 90) {
    errors.push('Tilt angle must be between 0° and 90°');
  } else if (tilt < 10) {
    warnings.push('Tilt angle is very low (< 10°). This may reduce energy production.');
  } else if (tilt > 45) {
    warnings.push('Tilt angle is high (> 45°). Verify this is intentional for your latitude.');
  }

  return { errors, warnings };
}

/**
 * Validate azimuth angle (degrees)
 */
function validateAzimuth(azimuth: number | undefined): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (azimuth === undefined || azimuth === null) {
    errors.push('Azimuth angle is required');
    return { errors, warnings };
  }

  if (typeof azimuth !== 'number' || isNaN(azimuth)) {
    errors.push('Azimuth angle must be a valid number');
    return { errors, warnings };
  }

  if (azimuth < 0 || azimuth > 360) {
    errors.push('Azimuth angle must be between 0° and 360°');
  } else if (azimuth < 135 || azimuth > 225) {
    warnings.push('Azimuth is not facing south (180°). This may reduce energy production.');
  }

  return { errors, warnings };
}

/**
 * Validate latitude (decimal degrees)
 */
function validateLatitude(latitude: number | undefined): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (latitude === undefined || latitude === null) {
    errors.push('Latitude is required');
    return { errors, warnings };
  }

  if (typeof latitude !== 'number' || isNaN(latitude)) {
    errors.push('Latitude must be a valid number');
    return { errors, warnings };
  }

  if (latitude < -90 || latitude > 90) {
    errors.push('Latitude must be between -90° and 90°');
  } else if (latitude < 15 || latitude > 35) {
    warnings.push('Location is outside Saudi Arabia. Weather data accuracy may vary.');
  }

  return { errors, warnings };
}

/**
 * Validate longitude (decimal degrees)
 */
function validateLongitude(longitude: number | undefined): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (longitude === undefined || longitude === null) {
    errors.push('Longitude is required');
    return { errors, warnings };
  }

  if (typeof longitude !== 'number' || isNaN(longitude)) {
    errors.push('Longitude must be a valid number');
    return { errors, warnings };
  }

  if (longitude < -180 || longitude > 180) {
    errors.push('Longitude must be between -180° and 180°');
  } else if (longitude < 34 || longitude > 56) {
    warnings.push('Location is outside Saudi Arabia. Weather data accuracy may vary.');
  }

  return { errors, warnings };
}

/**
 * Validate altitude (meters)
 */
function validateAltitude(altitude: number | undefined): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (altitude === undefined || altitude === null) {
    // Altitude is optional, default to 0
    return { errors, warnings };
  }

  if (typeof altitude !== 'number' || isNaN(altitude)) {
    errors.push('Altitude must be a valid number');
    return { errors, warnings };
  }

  if (altitude < -500 || altitude > 5000) {
    errors.push('Altitude must be between -500m and 5000m');
  } else if (altitude > 3000) {
    warnings.push('Altitude is very high (> 3000m). Verify location.');
  }

  return { errors, warnings };
}

/**
 * Validate monthly consumption (kWh per month)
 */
function validateMonthlyConsumption(consumption: number[] | undefined): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!consumption) {
    // Monthly consumption is optional
    return { errors, warnings };
  }

  if (!Array.isArray(consumption)) {
    errors.push('Monthly consumption must be an array');
    return { errors, warnings };
  }

  if (consumption.length !== 12) {
    errors.push('Monthly consumption must have exactly 12 values (one per month)');
    return { errors, warnings };
  }

  consumption.forEach((value, index) => {
    if (typeof value !== 'number' || isNaN(value)) {
      errors.push(`Month ${index + 1} consumption must be a valid number`);
    } else if (value < 0) {
      errors.push(`Month ${index + 1} consumption cannot be negative`);
    } else if (value === 0) {
      warnings.push(`Month ${index + 1} consumption is zero. Is this correct?`);
    } else if (value > 1000000) {
      warnings.push(`Month ${index + 1} consumption is very high (> 1,000,000 kWh). Please verify.`);
    }
  });

  return { errors, warnings };
}

/**
 * Validate yearly consumption (kWh per year)
 */
function validateYearlyConsumption(consumption: number | undefined): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!consumption) {
    // Yearly consumption is optional
    return { errors, warnings };
  }

  if (typeof consumption !== 'number' || isNaN(consumption)) {
    errors.push('Yearly consumption must be a valid number');
    return { errors, warnings };
  }

  if (consumption < 0) {
    errors.push('Yearly consumption cannot be negative');
  } else if (consumption === 0) {
    warnings.push('Yearly consumption is zero. Is this correct?');
  } else if (consumption > 12000000) {
    warnings.push('Yearly consumption is very high (> 12,000,000 kWh). Please verify.');
  }

  return { errors, warnings };
}

/**
 * Validate cable distance (meters)
 */
function validateCableDistance(distance: number | undefined, type: 'DC' | 'AC'): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!distance) {
    // Cable distance is optional (has defaults)
    return { errors, warnings };
  }

  if (typeof distance !== 'number' || isNaN(distance)) {
    errors.push(`${type} cable distance must be a valid number`);
    return { errors, warnings };
  }

  if (distance < 0) {
    errors.push(`${type} cable distance cannot be negative`);
  } else if (distance === 0) {
    warnings.push(`${type} cable distance is zero. Is this correct?`);
  } else if (distance > 1000) {
    warnings.push(`${type} cable distance is very long (> 1000m). This may cause significant voltage drop.`);
  }

  return { errors, warnings };
}

/**
 * Validate module power (Watts)
 */
function validateModulePower(power: number | undefined): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!power) {
    // Module power is optional (has default)
    return { errors, warnings };
  }

  if (typeof power !== 'number' || isNaN(power)) {
    errors.push('Module power must be a valid number');
    return { errors, warnings };
  }

  if (power <= 0) {
    errors.push('Module power must be greater than 0');
  } else if (power < 100) {
    warnings.push('Module power is very low (< 100W). Are you sure?');
  } else if (power > 1000) {
    warnings.push('Module power is very high (> 1000W). Please verify.');
  }

  return { errors, warnings };
}

/**
 * Validate email address
 */
function validateEmail(email: string | undefined): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!email) {
    // Email is optional
    return { errors, warnings };
  }

  if (typeof email !== 'string') {
    errors.push('Email must be a string');
    return { errors, warnings };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    errors.push('Invalid email format');
  }

  return { errors, warnings };
}

/**
 * Validate phone number
 */
function validatePhoneNumber(phone: string | undefined): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!phone) {
    // Phone is optional
    return { errors, warnings };
  }

  if (typeof phone !== 'string') {
    errors.push('Phone number must be a string');
    return { errors, warnings };
  }

  // Remove spaces, dashes, and parentheses
  const cleanPhone = phone.replace(/[\s\-()]/g, '');
  
  // Check if it's a valid phone number (8-15 digits, optionally starting with +)
  const phoneRegex = /^\+?[0-9]{8,15}$/;
  if (!phoneRegex.test(cleanPhone)) {
    errors.push('Invalid phone number format');
  }

  return { errors, warnings };
}

/**
 * Validate all system inputs
 */
export function validateSystemInputs(inputs: SystemInputs): ValidationResult {
  const allErrors: string[] = [];
  const allWarnings: string[] = [];

  // Validate each field
  const validations = [
    validateSystemSize(inputs.systemSize),
    validateTilt(inputs.tilt),
    validateAzimuth(inputs.azimuth),
    validateLatitude(inputs.latitude),
    validateLongitude(inputs.longitude),
    validateAltitude(inputs.altitude),
    validateMonthlyConsumption(inputs.monthlyConsumption),
    validateYearlyConsumption(inputs.yearlyConsumption),
    validateCableDistance(inputs.dcCableDistance, 'DC'),
    validateCableDistance(inputs.acCableDistance, 'AC'),
    validateModulePower(inputs.modulePower),
    validateEmail(inputs.email),
    validatePhoneNumber(inputs.phoneNumber),
  ];

  // Collect all errors and warnings
  validations.forEach(({ errors, warnings }) => {
    allErrors.push(...errors);
    allWarnings.push(...warnings);
  });

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
    warnings: allWarnings,
  };
}

/**
 * Sanitize inputs (apply defaults and corrections)
 */
export function sanitizeSystemInputs(inputs: SystemInputs): SystemInputs {
  return {
    ...inputs,
    systemSize: inputs.systemSize && inputs.systemSize > 0 ? inputs.systemSize : undefined,
    tilt: inputs.tilt !== undefined ? Math.max(0, Math.min(90, inputs.tilt)) : undefined,
    azimuth: inputs.azimuth !== undefined ? Math.max(0, Math.min(360, inputs.azimuth)) : undefined,
    latitude: inputs.latitude !== undefined ? Math.max(-90, Math.min(90, inputs.latitude)) : undefined,
    longitude: inputs.longitude !== undefined ? Math.max(-180, Math.min(180, inputs.longitude)) : undefined,
    altitude: inputs.altitude !== undefined ? Math.max(-500, Math.min(5000, inputs.altitude)) : 0,
    dcCableDistance: inputs.dcCableDistance && inputs.dcCableDistance > 0 ? inputs.dcCableDistance : undefined,
    acCableDistance: inputs.acCableDistance && inputs.acCableDistance > 0 ? inputs.acCableDistance : undefined,
    modulePower: inputs.modulePower && inputs.modulePower > 0 ? inputs.modulePower : undefined,
  };
}

/**
 * Validate calculation inputs (for solarCalculationEngine)
 */
export function validateCalculationInputs(inputs: CalculationInputs): ValidationResult {
  const allErrors: string[] = [];
  const allWarnings: string[] = [];

  // Validate each field using existing validators
  const validations = [
    validateSystemSize(inputs.systemSize),
    validateTilt(inputs.tilt),
    validateAzimuth(inputs.azimuth),
    validateLatitude(inputs.latitude),
    validateLongitude(inputs.longitude),
    validateAltitude(inputs.altitude),
    validateMonthlyConsumption(inputs.monthlyConsumption),
    validateYearlyConsumption(inputs.yearlyConsumption),
    validateModulePower(inputs.modulePower),
  ];

  // Collect all errors and warnings
  validations.forEach(({ errors, warnings }) => {
    allErrors.push(...errors);
    allWarnings.push(...warnings);
  });

  // Validate mountingType
  if (!inputs.mountingType || typeof inputs.mountingType !== 'string') {
    allErrors.push('Mounting type is required');
  }

  // Validate surfaceType
  if (!inputs.surfaceType || typeof inputs.surfaceType !== 'string') {
    allErrors.push('Surface type is required');
  }

  // Validate shadingLevel if provided
  if (inputs.shadingLevel !== undefined) {
    const validShadingLevels = ['none', 'partial', 'significant'];
    if (!validShadingLevels.includes(inputs.shadingLevel)) {
      allErrors.push(`Shading level must be one of: ${validShadingLevels.join(', ')}`);
    }
  }

  // Validate userType if provided
  if (inputs.userType !== undefined) {
    const validUserTypes = ['residential', 'commercial', 'industrial', 'agricultural'];
    if (!validUserTypes.includes(inputs.userType)) {
      allErrors.push(`User type must be one of: ${validUserTypes.join(', ')}`);
    }
  }

  // Validate soilingLoss if provided
  if (inputs.soilingLoss !== undefined) {
    if (typeof inputs.soilingLoss !== 'number' || isNaN(inputs.soilingLoss)) {
      allErrors.push('Soiling loss must be a valid number');
    } else if (inputs.soilingLoss < 0 || inputs.soilingLoss > 1) {
      allErrors.push('Soiling loss must be between 0 and 1');
    }
  }

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
    warnings: allWarnings,
  };
}